import { getCurrentInstance } from "vue";
import { toast } from "vue3-toastify";
import 'vue3-toastify/dist/index.css';
import apiClient from "@/axios";

const API_PATHS = {
    LINKS: 'links',
    CLAIM_LINK: 'links/{slug}/claim',
};

const state = {
    links: null,
    link: null,
    error: null,
    isLoading: false,
};

const getters = {
    stateLinks: state => state.links,
    stateLink: state => state.link,
    stateLinkError: state => state.error,
    stateIsLoading: state => state.isLoading,
};

const actions = {
    async getLinks({ commit }) {
        try {
            commit('setLoading', true);
            const response = await apiClient.get(API_PATHS.LINKS+'/');
            commit('setLinks', response.data);
        } catch (error) {
            console.error('Error fetching links:', error);
            let errorMessage = "Failed to load links.";
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            }
            commit('setError', errorMessage);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },

    async getLink({ commit }, slug) {
        commit('setLoading', true);
        try {
            let link = await apiClient.get(`${API_PATHS.LINKS}/${slug}`);
            commit('setLink', link.data);
        } catch (error) {
            console.log(error);
            let errorMessage = "Failed to load link.";
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            }
            commit('setError', errorMessage);
        } finally {
            commit('setLoading', false);
        }
    },

    async createLink({ commit }, { slug, type = 'redirect', redirectTo, fileId }) {
        try {
            commit('setLoading', true);
            const payload = {
                slug,
                type,
                redirect_to: redirectTo,
                file_id: fileId
            };
            
            const response = await apiClient.post(API_PATHS.LINKS+'/', payload);
            commit('addLink', response.data);
            toast.success('Link created successfully');
            return response.data;
        } catch (error) {
            console.error('Error creating link:', error);
            let errorMessage = "Failed to create link.";
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            }
            commit('setError', errorMessage);
            toast.error(errorMessage);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },

    async deleteLink({ commit }, slug) {
        try {
            commit('setLoading', true);
            await apiClient.delete(`${API_PATHS.LINKS}/${slug}`);
            commit('removeLink', slug);
            toast.success('Link deleted successfully');
        } catch (error) {
            console.error('Error deleting link:', error);
            let errorMessage = "Failed to delete link.";
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            }
            commit('setError', errorMessage);
            toast.error(errorMessage);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },

    async claimLink({ commit }, slug) {
        try {
            commit('setLoading', true);
            const response = await apiClient.post(
                API_PATHS.CLAIM_LINK.replace('{slug}', slug)
            );
            commit('updateLink', response.data);
            toast.success('Link claimed successfully');
            return response.data;
        } catch (error) {
            console.error('Error claiming link:', error);
            let errorMessage = "Failed to claim link.";
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            }
            commit('setError', errorMessage);
            toast.error(errorMessage);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },

    showErrorToast(message) {
        const vueInstance = getCurrentInstance();
        if (vueInstance) {
            vueInstance.appContext.config.globalProperties.$toast.error(message);
        }
    }
};

const mutations = {
    setLinks(state, links) {
        state.links = links;
    },
    setLink(state, link) {
        state.link = link;
    },
    addLink(state, link) {
        if (!state.links) state.links = [];
        state.links.unshift(link);
    },
    updateLink(state, updatedLink) {
        if (state.links) {
            const index = state.links.findIndex(link => link.slug === updatedLink.slug);
            if (index !== -1) {
                state.links.splice(index, 1, updatedLink);
            }
        }
        if (state.link && state.link.slug === updatedLink.slug) {
            state.link = updatedLink;
        }
    },
    removeLink(state, slug) {
        if (state.links) {
            state.links = state.links.filter(link => link.slug !== slug);
        }
        if (state.link && state.link.slug === slug) {
            state.link = null;
        }
    },
    setError(state, error) {
        state.error = error;
    },
    setLoading(state, isLoading) {
        state.isLoading = isLoading;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
